import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    Loader2,
    Lock,
    ChevronDown,
    ChevronUp,
    Save,
    Share2,
    CheckCircle2,
    Users2,
    FileText,
    TrendingUp,
    Paperclip,
    Trash2,
    Check,
    AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

function bandColor(v) {
    if (v == null) return "#94A3B8";
    if (v < 1.5) return "#DC2626";
    if (v < 2.5) return "#D97706";
    if (v < 3.5) return "#1E56A0";
    return "#059669";
}
function descriptorFor(v) {
    if (v == null) return "Not yet rated";
    if (v < 1.5) return "Minimal/Fragmented";
    if (v < 2.5) return "Emerging/Inconsistent";
    if (v < 3.5) return "Operational/Aligned";
    return "Highly Coherent/Adaptive";
}

export default function LrfReflectionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [editingPeriod, setEditingPeriod] = useState(false);
    const [periodDraft, setPeriodDraft] = useState("");
    const [shareOpen, setShareOpen] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);

    // Client-side state for fast auto-save
    const [ratings, setRatings] = useState({});   // obj_id → {rating, notes}
    const [responses, setResponses] = useState({}); // q_id → text
    const saveTimers = useRef({});
    const savedResetTimer = useRef(null);
    const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/lrf/reflections/${id}`);
                setPayload(data);
                const r = {};
                for (const row of data.objective_ratings) r[row.lrf_objective_id] = { rating: row.rating, notes: row.notes || "" };
                setRatings(r);
                const q = {};
                for (const row of data.question_responses) q[row.lrf_question_id] = row.response_text || "";
                setResponses(q);
                setPeriodDraft(data.reflection.period_label);
                // Auto-expand first domain for quick access
                if (data.framework?.[0]?.id) setExpanded({ [data.framework[0].id]: true });
            } catch (e) {
                toast.error(extractError(e, "Could not load reflection"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const framework = payload?.framework || [];
    const canEdit = payload?.can_edit;
    const isLocked = payload?.reflection?.status === "complete";

    const domainAverages = useMemo(() => {
        const out = {};
        for (const d of framework) {
            const vals = d.objectives.map((o) => ratings[o.id]?.rating).filter((v) => v != null);
            out[d.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
        }
        return out;
    }, [framework, ratings]);

    const overall = useMemo(() => {
        const vals = Object.values(domainAverages).filter((v) => v != null);
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }, [domainAverages]);

    const progress = useMemo(() => {
        let ratedObjs = 0, filledQs = 0, totalQs = 0;
        for (const d of framework) {
            for (const o of d.objectives) {
                if (ratings[o.id]?.rating != null) ratedObjs++;
                for (const q of o.questions) {
                    totalQs++;
                    if ((responses[q.id] || "").trim()) filledQs++;
                }
            }
        }
        return { ratedObjs, filledQs, totalQs };
    }, [framework, ratings, responses]);

    // ---- Auto-save helpers
    const markSaved = useCallback(() => {
        setSaveStatus("saved");
        if (savedResetTimer.current) clearTimeout(savedResetTimer.current);
        savedResetTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
    }, []);

    const flushRating = useCallback(async (objId, next) => {
        setSaveStatus("saving");
        try {
            await api.put(`/lrf/reflections/${id}/objectives/${objId}/rating`, next);
            markSaved();
        } catch (e) {
            setSaveStatus("error");
            toast.error(extractError(e, "Rating save failed"));
        }
    }, [id, markSaved]);

    const flushResponse = useCallback(async (qId, text) => {
        setSaveStatus("saving");
        try {
            await api.put(`/lrf/reflections/${id}/questions/${qId}/response`, { response_text: text });
            markSaved();
        } catch (e) {
            setSaveStatus("error");
            toast.error(extractError(e, "Response save failed"));
        }
    }, [id, markSaved]);

    const scheduleSave = useCallback((key, fn) => {
        if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
        saveTimers.current[key] = setTimeout(fn, 800);
    }, []);

    function updateRating(objId, patch) {
        if (!canEdit) return;
        const next = { ...(ratings[objId] || { rating: null, notes: "" }), ...patch };
        setRatings((prev) => ({ ...prev, [objId]: next }));
        scheduleSave(`r:${objId}`, () => flushRating(objId, next));
    }

    function updateResponse(qId, text) {
        if (!canEdit) return;
        setResponses((prev) => ({ ...prev, [qId]: text }));
        scheduleSave(`q:${qId}`, () => flushResponse(qId, text));
    }

    async function savePeriod() {
        if (!periodDraft.trim()) return;
        try {
            const { data } = await api.put(`/lrf/reflections/${id}`, { period_label: periodDraft.trim() });
            setPayload((p) => ({ ...p, reflection: { ...p.reflection, ...data } }));
            setEditingPeriod(false);
        } catch (e) {
            toast.error(extractError(e, "Rename failed"));
        }
    }

    async function markComplete() {
        try {
            const { data } = await api.post(`/lrf/reflections/${id}/complete`);
            setPayload((p) => ({ ...p, reflection: { ...p.reflection, ...data }, can_edit: false }));
            toast.success(data.partial ? `Reflection marked complete (${data.unrated_objectives} objectives unrated)` : "Reflection complete");
        } catch (e) {
            toast.error(extractError(e, "Could not complete"));
        }
    }

    async function downloadPdf() {
        try {
            setPdfBusy(true);
            const resp = await api.get(`/lrf/reflections/${id}/report.pdf`, { responseType: "blob" });
            const cd = resp.headers?.["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/.exec(cd);
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = match?.[1] || "LRF-Reflection.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(extractError(e, "Download failed"));
        } finally {
            setPdfBusy(false);
        }
    }

    if (loading) {
        return (
            <div className="text-[#0B2545] inline-flex items-center gap-2" data-testid="lrf-loading">
                <Loader2 className="animate-spin" size={16} /> Loading reflection…
            </div>
        );
    }
    if (!payload) return null;

    return (
        <div className="space-y-6" data-testid="lrf-reflection">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to="/app/lrf" className="text-xs text-[#1E56A0] hover:underline">← Back to Reflections</Link>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        {editingPeriod ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    value={periodDraft}
                                    onChange={(e) => setPeriodDraft(e.target.value)}
                                    className="w-56"
                                    autoFocus
                                    data-testid="period-input"
                                />
                                <Button size="sm" onClick={savePeriod} data-testid="period-save">Save</Button>
                                <Button size="sm" variant="outline" onClick={() => { setEditingPeriod(false); setPeriodDraft(payload.reflection.period_label); }}>Cancel</Button>
                            </div>
                        ) : (
                            <h1
                                className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight cursor-pointer hover:underline decoration-dotted"
                                onClick={() => canEdit && setEditingPeriod(true)}
                                data-testid="lrf-period-label"
                                title={canEdit ? "Click to rename" : ""}
                            >
                                {payload.reflection.period_label}
                            </h1>
                        )}
                        <Badge className={(isLocked ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200") + " border"} variant="outline">
                            {isLocked && <Lock size={11} className="mr-1" />}
                            {isLocked ? "Complete" : "Draft"}
                        </Badge>
                        {canEdit && (
                            <span
                                className="text-xs inline-flex items-center gap-1 min-w-[80px]"
                                data-testid="lrf-save-indicator"
                                data-save-state={saveStatus}
                            >
                                {saveStatus === "saving" && (
                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                        <Loader2 size={12} className="animate-spin" /> Saving…
                                    </span>
                                )}
                                {saveStatus === "saved" && (
                                    <span className="inline-flex items-center gap-1 text-emerald-700">
                                        <Check size={12} /> Saved
                                    </span>
                                )}
                                {saveStatus === "error" && (
                                    <span className="inline-flex items-center gap-1 text-rose-700">
                                        <AlertCircle size={12} /> Save failed
                                    </span>
                                )}
                                {saveStatus === "idle" && (
                                    <span className="text-[#0E1B2C]/50">Auto-saves as you type</span>
                                )}
                            </span>
                        )}
                    </div>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70">
                        Owner: {payload.owner?.full_name || payload.owner?.email} · {(payload.reflection.shared_with || []).length} shared viewer(s)
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    {canEdit && (
                        <Button variant="outline" onClick={() => setShareOpen(true)} className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white" data-testid="lrf-share-btn">
                            <Share2 size={14} /> Share
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => navigate(`/app/lrf/reflections/${id}/summary`)} className="rounded-full" data-testid="lrf-open-summary-btn">
                        <TrendingUp size={14} /> Summary
                    </Button>
                    <Button onClick={downloadPdf} disabled={pdfBusy} className="rounded-full bg-[#0B2545] text-white hover:bg-[#1E56A0]" data-testid="lrf-download-pdf-btn">
                        {pdfBusy ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                        Download PDF
                    </Button>
                    {canEdit && (
                        <Button onClick={markComplete} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="lrf-complete-btn">
                            <CheckCircle2 size={14} /> Complete
                        </Button>
                    )}
                </div>
            </div>

            <ProgressBar overall={overall} progress={progress} />

            <div className="space-y-4" data-testid="lrf-domain-accordions">
                {framework.map((d) => (
                    <DomainAccordion
                        key={d.id}
                        domain={d}
                        expanded={!!expanded[d.id]}
                        onToggle={() => setExpanded((p) => ({ ...p, [d.id]: !p[d.id] }))}
                        ratings={ratings}
                        responses={responses}
                        onRating={updateRating}
                        onResponse={updateResponse}
                        canEdit={canEdit}
                        domainAvg={domainAverages[d.key]}
                        reflectionId={id}
                        evidence={payload.evidence}
                        onEvidenceChange={async () => {
                            const { data } = await api.get(`/lrf/reflections/${id}`);
                            setPayload((p) => ({ ...p, evidence: data.evidence }));
                        }}
                    />
                ))}
            </div>

            {shareOpen && (
                <ShareDialog
                    reflectionId={id}
                    open={shareOpen}
                    setOpen={setShareOpen}
                    currentShared={payload.reflection.shared_with || []}
                    onSaved={(next) => setPayload((p) => ({ ...p, reflection: { ...p.reflection, shared_with: next } }))}
                />
            )}
        </div>
    );
}

function ProgressBar({ overall, progress }) {
    const pctObjs = Math.round((progress.ratedObjs / 36) * 100);
    const pctQs = Math.round((progress.filledQs / progress.totalQs || 1) * 100);
    const color = bandColor(overall);
    return (
        <div className="rounded-2xl border border-[#D6E6F2] bg-white p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="lrf-progress">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Overall (live)</div>
                <div className="mt-1 font-serif text-4xl font-semibold leading-none" style={{ color }} data-testid="lrf-overall-score">
                    {overall == null ? "—" : overall.toFixed(2)}
                </div>
                <div className="text-sm text-[#0E1B2C]/70 mt-1">{descriptorFor(overall)}</div>
            </div>
            <ProgressStat label="Objectives rated" value={progress.ratedObjs} total={36} pct={pctObjs} testid="progress-objs" />
            <ProgressStat label="Questions answered" value={progress.filledQs} total={progress.totalQs} pct={pctQs} testid="progress-qs" />
        </div>
    );
}

function ProgressStat({ label, value, total, pct, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">{label}</div>
            <div className="mt-1 font-serif text-2xl font-semibold text-[#0B2545]">
                {value} <span className="text-sm text-[#0E1B2C]/60 font-normal">/ {total}</span>
            </div>
            <div className="mt-2 h-1.5 bg-[#E5EEF7] rounded-full overflow-hidden">
                <div className="h-full bg-[#1E56A0] transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
        </div>
    );
}

function DomainAccordion({ domain, expanded, onToggle, ratings, responses, onRating, onResponse, canEdit, domainAvg, reflectionId, evidence, onEvidenceChange }) {
    const rated = domain.objectives.filter((o) => ratings[o.id]?.rating != null).length;
    return (
        <div className="rounded-2xl border border-[#D6E6F2] bg-white overflow-hidden" data-testid={`domain-acc-${domain.key}`}>
            <button
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#F1F6FB]"
                onClick={onToggle}
                data-testid={`domain-toggle-${domain.key}`}
            >
                <div className="flex items-center gap-3">
                    <div className="w-2 h-8 rounded-full" style={{ backgroundColor: domain.color_hex }} />
                    <div className="text-left">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: domain.color_hex }}>
                            Domain {domain.position} · {rated}/6 rated
                        </div>
                        <div className="font-serif text-lg font-semibold text-[#0B2545]">{domain.name}</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="font-serif text-xl font-semibold" style={{ color: bandColor(domainAvg) }} data-testid={`domain-avg-${domain.key}`}>
                        {domainAvg == null ? "—" : domainAvg.toFixed(2)}
                    </div>
                    {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
            </button>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-5 space-y-4">
                            {domain.objectives.map((o) => (
                                <ObjectiveBlock
                                    key={o.id}
                                    objective={o}
                                    domain={domain}
                                    rating={ratings[o.id]?.rating ?? null}
                                    notes={ratings[o.id]?.notes ?? ""}
                                    responses={responses}
                                    canEdit={canEdit}
                                    onRating={(patch) => onRating(o.id, patch)}
                                    onResponse={(qId, text) => onResponse(qId, text)}
                                    reflectionId={reflectionId}
                                    evidence={evidence.filter((e) => e.lrf_objective_id === o.id)}
                                    onEvidenceChange={onEvidenceChange}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function ObjectiveBlock({ objective, domain, rating, notes, responses, canEdit, onRating, onResponse, reflectionId, evidence, onEvidenceChange }) {
    const [showLookFors, setShowLookFors] = useState(false);
    return (
        <div className="rounded-xl border border-[#D6E6F2] bg-[#FAFCFF]" data-testid={`obj-block-${domain.key}-${objective.position}`}>
            <div className="p-4 space-y-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: domain.color_hex }}>
                        Objective {objective.position}
                    </div>
                    <div className="font-serif text-lg font-semibold text-[#0B2545]">{objective.name}</div>
                    <p className="text-sm text-[#0E1B2C]/80 italic mt-1">{objective.purpose}</p>
                </div>

                <button
                    onClick={() => setShowLookFors((v) => !v)}
                    className="text-xs text-[#1E56A0] hover:underline"
                    data-testid={`toggle-lookfors-${domain.key}-${objective.position}`}
                >
                    {showLookFors ? "Hide" : "Show"} Look-Fors ({objective.look_fors.length})
                </button>
                {showLookFors && (
                    <ul className="list-disc pl-5 space-y-1 text-sm text-[#0E1B2C]/90">
                        {objective.look_fors.map((lf) => <li key={lf.id}>{lf.text}</li>)}
                    </ul>
                )}

                <div className="border-t border-[#D6E6F2] pt-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Self-rating (1–4)</div>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid={`rating-group-${domain.key}-${objective.position}`}>
                        {[1, 2, 3, 4].map((n) => (
                            <button
                                key={n}
                                type="button"
                                disabled={!canEdit}
                                onClick={() => onRating({ rating: rating === n ? null : n, notes })}
                                data-testid={`rating-${domain.key}-${objective.position}-${n}`}
                                className={`w-11 h-11 rounded-lg font-serif text-lg font-semibold transition-all border ${
                                    rating === n
                                        ? "bg-[#0B2545] border-[#0B2545] text-white"
                                        : "bg-white border-[#D6E6F2] text-[#0B2545] hover:border-[#1E56A0]"
                                } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                                {n}
                            </button>
                        ))}
                        {rating != null && canEdit && (
                            <button
                                type="button"
                                onClick={() => onRating({ rating: null, notes })}
                                className="text-xs text-[#0E1B2C]/60 hover:underline"
                                data-testid={`clear-rating-${domain.key}-${objective.position}`}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <Textarea
                        disabled={!canEdit}
                        placeholder="Notes about your rating (optional)"
                        value={notes}
                        onChange={(e) => onRating({ rating, notes: e.target.value })}
                        className="mt-2 min-h-[60px]"
                        data-testid={`rating-notes-${domain.key}-${objective.position}`}
                    />
                </div>

                <div className="border-t border-[#D6E6F2] pt-3 space-y-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Reflection Questions</div>
                    {objective.questions.map((q) => (
                        <div key={q.id}>
                            <div className="text-sm font-semibold text-[#0B2545]">
                                {q.position}. {q.text}
                            </div>
                            <Textarea
                                disabled={!canEdit}
                                placeholder="Your response…"
                                value={responses[q.id] || ""}
                                onChange={(e) => onResponse(q.id, e.target.value)}
                                className="mt-1 min-h-[70px]"
                                data-testid={`response-${domain.key}-${objective.position}-${q.position}`}
                            />
                        </div>
                    ))}
                </div>

                <EvidenceSection
                    canEdit={canEdit}
                    reflectionId={reflectionId}
                    objectiveId={objective.id}
                    domainKey={domain.key}
                    objectivePosition={objective.position}
                    evidence={evidence}
                    onChange={onEvidenceChange}
                />
            </div>
        </div>
    );
}

function EvidenceSection({ canEdit, reflectionId, objectiveId, domainKey, objectivePosition, evidence, onChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function handleFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append("file", f);
            fd.append("evidence_label", "");
            await api.post(`/lrf/reflections/${reflectionId}/objectives/${objectiveId}/evidence`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Evidence uploaded");
            onChange && onChange();
        } catch (err) {
            toast.error(extractError(err, "Upload failed"));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    }

    async function handleDelete(evId) {
        try {
            await api.delete(`/lrf/reflections/${reflectionId}/evidence/${evId}`);
            onChange && onChange();
        } catch (err) {
            toast.error(extractError(err, "Delete failed"));
        }
    }

    return (
        <div className="border-t border-[#D6E6F2] pt-3">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Evidence</div>
                {canEdit && (
                    <>
                        <input ref={inputRef} type="file" className="hidden" onChange={handleFile} data-testid={`evidence-file-${domainKey}-${objectivePosition}`} />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="text-xs"
                            data-testid={`evidence-upload-${domainKey}-${objectivePosition}`}
                        >
                            {uploading ? <Loader2 className="animate-spin" size={12} /> : <Paperclip size={12} />}
                            Attach
                        </Button>
                    </>
                )}
            </div>
            {evidence.length === 0 ? (
                <div className="text-xs text-[#0E1B2C]/60 mt-1">No evidence attached yet.</div>
            ) : (
                <ul className="mt-2 space-y-1" data-testid={`evidence-list-${domainKey}-${objectivePosition}`}>
                    {evidence.map((e) => (
                        <li key={e.id} className="flex items-center gap-2 text-sm">
                            <Paperclip size={12} className="text-[#1E56A0]" />
                            <span className="truncate flex-1">{e.filename}</span>
                            <span className="text-xs text-[#0E1B2C]/60">{Math.round(e.size_bytes / 1024)} KB</span>
                            {canEdit && (
                                <button onClick={() => handleDelete(e.id)} className="text-red-500 hover:text-red-700" data-testid={`evidence-delete-${e.id}`}>
                                    <Trash2 size={12} />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ShareDialog({ reflectionId, open, setOpen, currentShared, onSaved }) {
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState(new Set(currentShared));
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/lrf/share-candidates");
                setCandidates(data);
            } catch (e) {
                toast.error(extractError(e, "Could not load users"));
            }
        })();
    }, []);

    async function save() {
        try {
            setSaving(true);
            const { data } = await api.put(`/lrf/reflections/${reflectionId}`, { shared_with: Array.from(selected) });
            onSaved(data.shared_with || []);
            toast.success("Sharing updated");
            setOpen(false);
        } catch (e) {
            toast.error(extractError(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent data-testid="share-dialog">
                <DialogHeader>
                    <DialogTitle>Share reflection (view-only)</DialogTitle>
                </DialogHeader>
                <div className="text-xs text-[#0E1B2C]/70">
                    Selected users can view this reflection but cannot edit it. Teachers are excluded automatically.
                </div>
                <div className="max-h-[60vh] overflow-y-auto space-y-1 pt-2">
                    {candidates.length === 0 ? (
                        <div className="text-sm text-[#0E1B2C]/60">No eligible users found.</div>
                    ) : (
                        candidates.map((c) => {
                            const checked = selected.has(c.id);
                            return (
                                <label
                                    key={c.id}
                                    className={`flex items-center gap-3 rounded-lg px-3 py-2 border cursor-pointer transition-colors ${checked ? "border-[#1E56A0] bg-[#F1F6FB]" : "border-transparent hover:border-[#D6E6F2]"}`}
                                    data-testid={`share-candidate-${c.id}`}
                                >
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={(v) => {
                                            const next = new Set(selected);
                                            if (v) next.add(c.id); else next.delete(c.id);
                                            setSelected(next);
                                        }}
                                        data-testid={`share-check-${c.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-[#0B2545] truncate">{c.full_name || c.email}</div>
                                        <div className="text-xs text-[#0E1B2C]/60 truncate">{c.email} · {c.role}</div>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-[#0B2545] text-white hover:bg-[#1E56A0]" data-testid="share-save-btn">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
